/**
 * Coach-facing copy for a HARD LIMIT — a check that can fail an attempt
 * outright rather than only marking it down (PR-5 of the coach-input refactor,
 * REFACTOR_PLAN.md §C.3c).
 *
 * The problem this surface exists for: a coach who says "never" or "must" is
 * saying the strongest thing they can say, and until now the studio had one
 * tick-box for it and no way to say WHERE the failure line sits. The only
 * number in the config was the strictness, which the scorer multiplies before
 * comparing — so a coach who meant "fail above 20 degrees" got a failure line
 * at 60. PR-5 asks for the failure angle itself, and the number the coach types
 * is the number the check compares against.
 *
 * SINGLE SOURCE of every string the studio's hard-limit control and the
 * checkpoint / suggestion cards render, so the copy gate in
 * `docs/COACH_FACING_COPY_STANDARD.md` §4 has one place to review and the
 * components stay layout-only. Same shape as `toleranceReviewCopy.ts`, which
 * owns the OTHER number on the same check.
 *
 * TWO DIFFERENT NUMBERS, DELIBERATELY KEPT APART:
 *   · strictness (`angle_tolerance_degrees`, `toleranceReviewCopy.ts`) —
 *     "how strict", how far off still scores well.
 *   · the failure angle (`coach_breach_degrees`, this module) —
 *     "when it's a failure", past which the attempt is ruled out.
 * A coach can set one without the other, and no string here mentions the
 * strictness number: conflating them is how "never" stopped meaning never.
 *
 * Every string answers the standard's three questions, in order: WHAT to do,
 * WHY in one sentence a coach would say to another coach, HOW concretely. No
 * internal vocabulary — pinned by `frontend/src/test/coachCopyDenylist.ts`.
 *
 * WHERE THE SENTENCES COME FROM: the three threshold sources and the three
 * failure shapes are the backend's (§C.3c). This module turns the `gate_summary`
 * it reports into the coach's words; it never DECIDES a shape or a source, and
 * nothing here second-guesses the scorer. Where the backend ships a whole
 * notice (`gate_needs_a_number`, `gate_not_possible_for_this_check`,
 * `gate_threshold_source`), its what / why / how is rendered VERBATIM and this
 * module composes nothing.
 */

import type { GateSummary, GateThresholdSource } from '@/types/api';

// ---------------------------------------------------------------------------
// The studio control: "At what angle is it a failure?"
// ---------------------------------------------------------------------------

/** The field's label — the question the coach is actually being asked. */
export const BREACH_ANGLE_FIELD_LABEL = 'At what angle is it a failure?';

/**
 * The explanation under the field, owner-specified wording.
 *
 * It is the WHY and the HOW in one sentence: the number is an example of the
 * thing the coach is about to type, and the consequence is stated in the same
 * breath.
 *
 * The §4.2 plain-language read flagged "count as a breach"; the owner decided
 * on 2026-10-09 that "breach" is not a coach word and the sentence says
 * "count as a failure" instead. That matches the field's own label and the
 * HOW below, which already say "failure", so the coach now reads one word for
 * one thing. "breach" is on both halves of the copy denylist
 * (`frontend/src/test/coachCopyDenylist.ts`, `backend/tests/coach_copy_denylist.py`)
 * so it cannot come back into coach-facing prose; the identifiers around it
 * (`coach_breach_degrees`, `breach_degrees`, these constant names) are
 * contract surfaces and stay as they are.
 */
export const BREACH_ANGLE_EXPLANATION =
  'Typing 45 means a stance more than 45 degrees off will count as a failure, ' +
  'and the score is capped from there.';

/** The HOW: what to do, including the honest option of doing nothing. */
export const BREACH_ANGLE_HOW =
  "Type the angle you'd call a failure, or leave it empty and this check will " +
  'mark the athlete down instead of ruling them out.';

/** The tick-box that turns an ordinary check into a hard limit. */
export const HARD_LIMIT_TOGGLE_LABEL =
  'Disqualify if the athlete faces the wrong way';

/**
 * The tick-box's own explanation.
 *
 * It replaces "The pass/fail line is learned from your good and disqualifying
 * example videos", which was true of a path that resolves for no template
 * today: the calibration source needs a minted boundary and none exists
 * (§C.3c's "Minted Kc ... yields a gate for ZERO live templates"). Promising a
 * coach a line we do not have is the class of untrue copy this refactor is
 * removing, so the sentence now points at the number they can actually set.
 */
export const HARD_LIMIT_TOGGLE_EXPLANATION =
  'When on, an athlete whose body is turned clearly differently from your ' +
  'reference loses the attempt. Use it for movements where facing the right ' +
  'way is non-negotiable, and type the angle you would call a failure below.';

// ---------------------------------------------------------------------------
// Which number is in force — the effective source, in coach words
// ---------------------------------------------------------------------------

/**
 * The one line that says WHOSE number decides a failure.
 *
 * Driven by `gate_summary.threshold_source`, which the backend resolves — the
 * studio never works it out from the config, because that would be a second,
 * independent decision about the same thing.
 */
export const GATE_THRESHOLD_SOURCE_LINES: Record<GateThresholdSource, string> = {
  coach: 'Using the number you typed',
  calibration: 'Using your calibration clips',
  none: "No failure angle set yet — this check grades, it doesn't fail",
};

export function gateThresholdSourceLine(source: GateThresholdSource): string {
  return GATE_THRESHOLD_SOURCE_LINES[source];
}

/**
 * The same line for a check the coach is still editing, which the backend has
 * not seen yet.
 *
 * This is NOT a second opinion about a saved check: `gate_summary` wins
 * whenever one exists, and the caller only reaches this when there is none —
 * i.e. before the first save, where the only fact available is whether the
 * coach has typed a number. `calibration` is unreachable here by construction;
 * only the backend can know a boundary was minted.
 */
export function unsavedGateThresholdSource(
  breachDegrees: number | null | undefined,
): Extract<GateThresholdSource, 'coach' | 'none'> {
  return typeof breachDegrees === 'number' ? 'coach' : 'none';
}

// ---------------------------------------------------------------------------
// Cards: what this check does when the athlete is off
// ---------------------------------------------------------------------------

/** No failure angle, and none coming: the check marks down rather than fails. */
export const NOT_A_HARD_LIMIT_SENTENCE = 'Not a hard limit — grades instead';

/** A hard limit with no number behind it yet. */
export const HARD_LIMIT_NEEDS_A_NUMBER_SENTENCE =
  'Hard limit — no failure angle set yet, so for now it grades instead';

/** A hard limit that rules the attempt out, with no angle to state. */
export const HARD_LIMIT_FAILS_SENTENCE =
  "Hard limit: fails the attempt when it's off";

/**
 * One line for a checkpoint / suggestion card, from the backend's own verdict.
 *
 * `shape` is the scorer's answer to "what happens when this is off", and the
 * three values are the three shapes of §C.3c:
 *   'ceiling' — the score is capped from the failure angle onwards, so the
 *               angle is worth saying out loud.
 *   'zero'    — the attempt is ruled out, and there is no angle to say: this
 *               shape compares a pass/fail with no boundary of its own.
 *   'none'    — not a hard limit at all.
 *
 * Returns `null` for an unrecognised shape so a card shows nothing rather than
 * a guess — a new shape added on the backend must reach this file to be
 * described, and must never be described wrongly in the meantime.
 */
export function gateSummarySentence(summary: GateSummary): string | null {
  switch (summary.shape) {
    case 'ceiling':
      return typeof summary.breach_degrees === 'number'
        ? `Hard limit at ${formatDegrees(summary.breach_degrees)}°`
        : HARD_LIMIT_NEEDS_A_NUMBER_SENTENCE;
    case 'zero':
      return HARD_LIMIT_FAILS_SENTENCE;
    case 'none':
      return NOT_A_HARD_LIMIT_SENTENCE;
    default:
      return null;
  }
}

/** Whole degrees read as whole degrees; a typed half-degree is kept. */
function formatDegrees(degrees: number): string {
  const rounded = Math.round(degrees * 10) / 10;
  return `${rounded}`;
}

// ---------------------------------------------------------------------------
// Reading a gate_summary off an untyped annotation
// ---------------------------------------------------------------------------

const GATE_SHAPES: readonly GateSummary['shape'][] = ['ceiling', 'zero', 'none'];
const GATE_SOURCES: readonly GateThresholdSource[] = ['coach', 'calibration', 'none'];

/**
 * Narrow an unknown value to a `GateSummary`.
 *
 * Needed because a compiled-rule suggestion's `annotation` is
 * `Record<string, unknown>` by contract (`types/ruleCompiler.ts`: it is
 * appended to the template VERBATIM and must not be re-typed field by field).
 * This reader is the one place that looks inside it, and it fails closed:
 * anything it does not recognise returns `null`, which renders nothing.
 */
export function readGateSummary(value: unknown): GateSummary | null {
  if (typeof value !== 'object' || value === null) return null;
  const candidate = value as Partial<Record<keyof GateSummary, unknown>>;

  const shape = candidate.shape;
  const thresholdSource = candidate.threshold_source;
  if (!GATE_SHAPES.includes(shape as GateSummary['shape'])) return null;
  if (!GATE_SOURCES.includes(thresholdSource as GateThresholdSource)) return null;

  const breach = candidate.breach_degrees;
  const breachDegrees =
    typeof breach === 'number' && Number.isFinite(breach) ? breach : null;

  return {
    shape: shape as GateSummary['shape'],
    threshold_source: thresholdSource as GateThresholdSource,
    breach_degrees: breachDegrees,
  };
}
